import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import {z} from 'zod';
import {getIndustryCategories} from '../../../core/repositories/reportsRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {ReportsByIndustryPage} from '../../../features/reports/components/ReportByIndustryPage';
import {withHandler} from '../../../server/token/withHandler';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  industryName?: string;
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
};

const QueryParameter = z.object({
  groupedIndustryId: z.string(),
});

type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const props: Props = {};

    const {groupedIndustryId} = context.validatedQuery;
    const {cookies} = context.req;

    const industryId = Number(groupedIndustryId);
    const industryCategories = await getIndustryCategories({});
    const industryName = industryCategories?.ok
      ? industryCategories.value.industryCategories.find(
          (item) => item.id === industryId,
        )?.name
      : '';

    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {
          ...props,
          industryName,
        },
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props: {
          ...props,
          industryName,
        },
      };
    }

    return {
      props: {
        ...props,
        industryName,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  industryName,
  userDataForAnalytics,
}: Props) => {
  return (
    <>
      <ReportsByIndustryPage industryName={industryName} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;

export default Page;
